import { forwardRef, useEffect, useMemo } from "react";
import { Stack, Select, TextInput, Textarea, Switch } from "@mantine/core";
import { useForm } from "@mantine/form";
import { required, scrollToFirstError } from "../../../utils/formValidation";

export interface ErrorFormValues {
  category: "accessioning" | "grossing" | "processing";
  error: string;
  description?: string;
  preventSignOff?: boolean;
  blockCase?: boolean;
}

interface ErrorFormProps {
  initialValues?: Partial<ErrorFormValues>;
  onSubmit: (values: ErrorFormValues) => void;
}

const ErrorForm = forwardRef<HTMLFormElement, ErrorFormProps>(
  ({ initialValues, onSubmit }, ref) => {
    const form = useForm<ErrorFormValues>({
      initialValues: {
        category: initialValues?.category || "accessioning",
        error: initialValues?.error || "",
        description: initialValues?.description || "",
        preventSignOff: initialValues?.preventSignOff ?? false,
        blockCase: initialValues?.blockCase ?? false,
      },
      validate: {
        category: required("Category is required"),
        error: required("Error name is required"),
      },
    });

    // Serialize initialValues to detect actual data changes, not just reference changes
    const initialValuesKey = useMemo(
      () => JSON.stringify(initialValues),
      [initialValues]
    );

    // Reset form when initialValues actually change (e.g., switching between edit/add)
    useEffect(() => {
      form.setValues({
        category: initialValues?.category || "accessioning",
        error: initialValues?.error || "",
        description: initialValues?.description || "",
        preventSignOff: initialValues?.preventSignOff ?? false,
        blockCase: initialValues?.blockCase ?? false,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValuesKey]);

    const handleSubmit = form.onSubmit(
      (values) => onSubmit(values),
      () => scrollToFirstError(form)
    );

    return (
      <form ref={ref} noValidate onSubmit={handleSubmit}>
        <Stack gap="md">
          <Select
            label="Category"
            placeholder="Select error category"
            data={[
              { value: "accessioning", label: "Accessioning" },
              { value: "grossing", label: "Grossing" },
              { value: "processing", label: "Processing" },
            ]}
            withAsterisk
            {...form.getInputProps("category")}
          />

          <TextInput
            label="Error Name"
            placeholder="Enter error name"
            withAsterisk
            {...form.getInputProps("error")}
          />

          <Textarea
            label="Description"
            placeholder="Enter detailed description"
            minRows={3}
            {...form.getInputProps("description")}
          />

          <Switch
            label="Prevent Sign Off"
            description="Prevent pathologist from signing off the case"
            {...form.getInputProps("preventSignOff", { type: "checkbox" })}
          />

          <Switch
            label="Block Case (Default)"
            description="Default setting for blocking the case from progressing (users can override)"
            {...form.getInputProps("blockCase", { type: "checkbox" })}
          />
        </Stack>
      </form>
    );
  }
);

ErrorForm.displayName = "ErrorForm";

export default ErrorForm;

// re-export for convenience

export { useForm } from "@mantine/form";
